import { ConfirmDialog } from 'components/confirm-dialog/confirm-dialog'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import timezones from 'timezones-list'
import {
	AppButton,
	Input,
	Checkbox,
	SearchableSelect,
	IntegrationLogo,
	Tab,
	TabList,
	Tabs,
	Tooltip
} from '@clickguard/design-system'
import { ProtectionSettingsCard } from 'components/protection-settings/protection-settings-card'
import {
	inheritsProtection,
	ownsProtection,
	protectionToSettings,
	settingsToProtection
} from 'components/protection-settings/protection-adapters'
import { useSignalCoverage } from 'components/protection-settings/signal-coverage'
import { ProtectionSettings } from 'components/protection-settings/protection-values'
import { ClickDetectionBanner } from './click-detection-banner'
import {
	AdAccountDTO,
	BingAdsAdAccountDTO,
	FacebookAdsAdAccountDTO
} from 'api-models'
import { useAccountStore, useAssetStore, useUiStore, useUserStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { AdAccounts } from 'services/user'
import { GoogleAuthConfig, BingAuthConfig, FacebookAuthConfig } from 'config'
import {
	adAccountLabel,
	currenciesOptions,
	formatAccountId,
	platformToProvider,
	transformPeriodToSeconds,
	transformSecondsToPeriod
} from './connection-helpers'
import { TrackingSetupCard } from './tracking-setup-card'
import {
	ConnectionsSheet,
	ConnectionSection,
	ConnectionSectionTitle
} from './connections-sheet'
import { HelpCircle } from 'components/icons'
import { AiOutlineQuestionCircle } from 'react-icons/ai'
import { TrackerCodeTemplate } from 'pages/workspace/asset/settings-page/tracking-section'
import React from 'react'

const tzList = timezones.map((t) => t.tzCode)
const PERIOD_UNITS = ['minutes', 'hours', 'days']

type AnyAdAccount = AdAccountDTO & {
	color?: string
	currency?: string
	timezone?: string
	details?: { account_number?: string; status?: string; link?: { id?: string } }
	preferences?: Record<string, any>
}

type Form = {
	name: string
	currency: string
	timezone: string
	accessLinkId: string
	ip_exclusions: boolean
	audience_exclusions: boolean
	set_custom_audience: boolean
	period: string
	periodUnit: string
	pixel_id: string
}

const authUrlFor = (platform: string) =>
	platform === 'GADS'
		? GoogleAuthConfig.AUTH_URL
		: platform === 'BADS'
		? BingAuthConfig.AUTH_URL
		: FacebookAuthConfig.AUTH_URL

/** OAuth provider id expected by `createOAuthLink` (differs from the logo id). */
const oauthProviderFor = (platform: string): 'google' | 'facebook' | 'bing' =>
	platform === 'GADS' ? 'google' : platform === 'BADS' ? 'bing' : 'facebook'

/**
 * localStorage key the OAuth callback pages stash the raw auth code under when
 * no re-auth flag is set (see pages/oauth/{google,bing,facebook}.tsx).
 */
const oauthCodeKeyFor = (platform: string): string =>
	platform === 'GADS'
		? 'google_oauth_code'
		: platform === 'BADS'
		? 'bing_oauth_code'
		: 'facebook_oauth_long_lived_token'

/** Preserve the account's current click-tracking mode on a details-only save. */
const currentClickTracking = (acc: AnyAdAccount): string => {
	const p = acc.preferences ?? {}
	if (p.automated_tracking && p.set_tracking_template) return 'BOTH'
	if (p.automated_tracking && !p.set_tracking_template) return 'AUTOMATIC'
	return 'MANUAL'
}

/**
 * Ad Account configuration drawer ("Account settings") — DEV-2352 §2.2,
 * cards 1 (Account details) + 3 (Danger Zone). The Ad-click-tracking card
 * (Google/Microsoft only) is added in Step 7.
 */
export const AdAccountDrawer = ({
	adAccount,
	open,
	onClose,
	initialTab = 'setup'
}: {
	adAccount: AdAccountDTO
	open: boolean
	onClose: () => void
	/**
	 * Which tab the drawer opens on.
	 *
	 * The gear icon on a row opens Setup, which is the whole drawer. The AI Protection
	 * page's "{n} with their own settings" panel opens `protection`: §1.1 says that row
	 * "opens that entity's protection settings", and the panel lists entities BECAUSE
	 * they own their protection — landing on Setup makes the reader hunt for the tab
	 * they asked for.
	 */
	initialTab?: 'setup' | 'protection'
}) => {
	const user = useUserStore((s) => s.user)
	const isLoading = useUiStore((s) => s.isLoading)
	const { account, userAccess, accountLinks, createOAuthLink } =
		useAccountStore(
			useShallow((s) => ({
				account: s.account,
				userAccess: s.userAccess,
				accountLinks: s.accountLinks,
				createOAuthLink: s.createOAuthLink
			}))
		)
	const {
		asset,
		demoMode,
		synchronizeAdAccount,
		deleteAdAccountAccount,
		setAdAccountProtection,
		updateSettingsAccountProtection,
		updateGadsAdAccountSettings,
		updateBadsAdAccountSettings,
		updateFadsAdAccountSettings,
		updateGadsSettingsAccessLink,
		updateBadsSettingsAccessLink,
		updateFadsSettingsAccessLink,
		refreshAdAccounts
	} = useAssetStore(
		useShallow((s) => ({
			asset: s.asset,
			demoMode: s.demoMode,
			synchronizeAdAccount: s.synchronizeAdAccount,
			deleteAdAccountAccount: s.deleteAdAccountAccount,
			setAdAccountProtection: s.setAdAccountProtection,
			updateSettingsAccountProtection: s.updateSettingsAccountProtection,
			updateGadsAdAccountSettings: s.updateGadsAdAccountSettings,
			updateBadsAdAccountSettings: s.updateBadsAdAccountSettings,
			updateFadsAdAccountSettings: s.updateFadsAdAccountSettings,
			updateGadsSettingsAccessLink: s.updateGadsSettingsAccessLink,
			updateBadsSettingsAccessLink: s.updateBadsSettingsAccessLink,
			updateFadsSettingsAccessLink: s.updateFadsSettingsAccessLink,
			refreshAdAccounts: s.refreshAdAccounts
		}))
	)

	const acc = adAccount as AnyAdAccount | null
	const platform = adAccount?.platform ?? 'GADS'
	const canWrite = !demoMode && userAccess(user?.id || '') !== 'READ'
	// Masks plan-gated signals out of the payload; the card already renders them off.
	const covers = useSignalCoverage()
	const [saving, setSaving] = useState(false)

	const [form, setForm] = useState<Form | null>(null)
	// Read-only now — the table row owns the write (§2.3), and so does the §2.4 cascade
	// below. DERIVED, not state: the form reset runs once per open, but click detection
	// can go off while the drawer is still up, and the banner has to follow it.
	const protectionOn = acc?.status === 'ACTIVE'
	const [tab, setTab] = useState(initialTab)
	// `protection.inherit === false` is the only reliable "has its own settings" test:
	// an ad account is CREATED with `protection: null`, so `!inherit` would read as
	// "owns its settings" for every inheriting account.
	const [inherit, setInherit] = useState(true)
	// The card owns its save state and portals into the fixed footer outside the body.
	const [saveSlot, setSaveSlot] = useState<HTMLElement | null>(null)
	const [dirty, setDirty] = useState(false)
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [awaitingCredential, setAwaitingCredential] = useState(false)
	const initialAccessId = useRef<string>('')

	const adPixels = useQuery(
		['adPixels', adAccount?.id],
		() => AdAccounts.getAccountAdPixels(account!.id, adAccount?.id),
		{ enabled: !!account && !!adAccount }
	)
	useEffect(() => {
		if (!open || !acc) return
		const p = acc.preferences ?? {}
		const accessId = acc.details?.link?.id ?? ''
		initialAccessId.current = accessId
		const seconds = Number(
			p.exclusion_period ?? p.audience_exclusion_period ?? 0
		)
		const conv = transformSecondsToPeriod(seconds || 0) as {
			period: string
			type?: string
		}
		setForm({
			name: acc.name ?? '',
			currency: acc.currency ?? '',
			timezone: acc.timezone ?? '',
			accessLinkId: accessId,
			ip_exclusions: !!p.ip_exclusions,
			audience_exclusions: !!p.audience_exclusions,
			set_custom_audience: !!p.set_custom_audience,
			period: platform === 'FADS' ? conv.period : String(seconds || 30),
			periodUnit: conv.type || 'days',
			// The selected pixel is persisted on the ad account details, not in
			// preferences — read it from there so it survives a failed/empty
			// live fetch of the available pixels (issue: pixel "disappears").
			pixel_id: (acc as FacebookAdsAdAccountDTO).details?.ads_pixel_id ?? ''
		})
		setInherit(inheritsProtection(acc))
		setTab(initialTab)
		setDirty(false)
		// Key the reset on the account id (and open) only — NOT the whole
		// adAccount object. Each save calls refreshAdAccounts(), which swaps the
		// object reference; depending on it here would re-run this effect
		// mid-save and clobber the user's in-flight edits (issue: credential
		// change not persisting / dropdown reverting).
	}, [open, adAccount?.id])

	// After "New credential" opens the OAuth tab, the callback page stashes the
	// raw auth code in localStorage (no re-auth flag is set from here). Poll for
	// it, then persist the credential via the store — which refreshes
	// accountLinks so the new connection appears in the dropdown below. Without
	// this the OAuth flow completes but no credential is ever created.
	useEffect(() => {
		if (!awaitingCredential) return
		const key = oauthCodeKeyFor(platform)
		const interval = setInterval(async () => {
			const code = window.localStorage.getItem(key)
			if (!code) return
			clearInterval(interval)
			window.localStorage.removeItem(key)
			try {
				// Keep the loading state up through BOTH the create (POST) and the
				// store's follow-up refreshAccountLinks (GET) — resetting before
				// these settle leaves a gap where the button looks idle while the
				// credential is still being added/fetched.
				await createOAuthLink(code, oauthProviderFor(platform))
			} catch {
				// Error toast already surfaced by the store.
			} finally {
				setAwaitingCredential(false)
			}
		}, 1000)
		return () => clearInterval(interval)
	}, [awaitingCredential, platform])

	const linkOptions = useMemo(
		() =>
			accountLinks
				.filter((l) => l.platform === platform)
				.map((l) => ({
					value: l.id,
					label:
						(l as any).name ||
						(l as any).email ||
						(l as any).account_number ||
						l.id
				})),
		[accountLinks, platform]
	)

	// Available pixels come live from the Facebook API; the list is empty when
	// the credential lost access, the fetch failed, or the account genuinely
	// has none. Hide the dropdown entirely in that case (issue: empty list),
	// but always keep the currently-selected pixel in the options so it renders
	// even if the live fetch omits it.
	const pixelList = adPixels.data ?? []
	const showPixel = pixelList.length > 0
	const pixelOptions = (
		form?.pixel_id && !pixelList.includes(form.pixel_id)
			? [form.pixel_id, ...pixelList]
			: pixelList
	).map((p) => ({ value: p, label: p }))

	if (!adAccount || !acc) {
		return (
			<ConnectionsSheet
				open={open}
				onClose={onClose}
				title="Ad account settings"
				tabs={null}
				footer={null}
			>
				{null}
			</ConnectionsSheet>
		)
	}

	const patch = (next: Partial<Form>) => {
		setForm((f) => (f ? { ...f, ...next } : f))
		setDirty(true)
	}

	// The website is the top of the chain, and what this account shows while it inherits.
	const websiteSettings = protectionToSettings(asset?.protection)

	const handleSaveProtection = async (next: ProtectionSettings | null) => {
		if (!account) return
		// `null` means "inherit from now on". The stored values still travel, because the
		// PATCH replaces the whole object — and `enabled: true` travels with them, since
		// `findProtectionConfig` reads `enabled` BEFORE `inherit`: an inheriting account
		// left at `enabled: false` gets no protection at all rather than the website's.
		const settings = next ?? websiteSettings
		await setAdAccountProtection(
			account.id,
			adAccount.id,
			settingsToProtection(settings, acc.protection, {
				inherit: next === null,
				covers
			})
		)

		// §2.4 — monitoring off with "Keep detecting clicks from this ad account"
		// unticked turns click detection off for THIS account, and nothing else under
		// the website. Same one-way trigger as §1.1: turning monitoring back on does not
		// re-enable it, and nothing records that this is what switched it off.
		//
		// `next` only, never the inherit branch: a save with the box ticked is the user
		// handing this account back to the website, not a decision about its clicks — and
		// the website's own save owns that cascade. The status guard keeps a re-save of an
		// already-off account from writing again (`toggle-active` also clears
		// `activated_at`, which the spend sync bills from).
		if (
			next &&
			!next.aiEnabled &&
			!next.keepTracking &&
			acc.status === 'ACTIVE'
		) {
			await updateSettingsAccountProtection(account.id, adAccount.id, {
				active: false
			})
		}
	}

	const handleSync = () => {
		if (!account) return
		synchronizeAdAccount(account.id, adAccount.id, platform.toLowerCase())
	}

	const handleSave = async () => {
		if (!account || !form) return
		setSaving(true)
		const base = {
			name: form.name,
			color: acc.color ?? '#2461FD',
			currency: form.currency,
			timezone: form.timezone
		}
		try {
			if (platform === 'GADS') {
				await updateGadsAdAccountSettings(account.id, adAccount.id, {
					...base,
					click_tracking: currentClickTracking(acc),
					ip_exclusions: form.ip_exclusions,
					audience_exclusions: form.audience_exclusions
				})
			} else if (platform === 'BADS') {
				await updateBadsAdAccountSettings(account.id, adAccount.id, {
					...base,
					set_custom_audience: form.set_custom_audience,
					exclusion_period: form.period,
					click_tracking: currentClickTracking(acc)
				} as any)
			} else {
				await updateFadsAdAccountSettings(account.id, adAccount.id, {
					...base,
					set_custom_audience: form.set_custom_audience,
					pixel_id: form.pixel_id,
					exclusion_period: transformPeriodToSeconds(
						form.period,
						form.periodUnit
					).toString()
				} as any)
			}

			if (form.accessLinkId && form.accessLinkId !== initialAccessId.current) {
				const payload = { link_id: form.accessLinkId }
				if (platform === 'GADS')
					await updateGadsSettingsAccessLink(account.id, adAccount.id, payload)
				else if (platform === 'BADS')
					await updateBadsSettingsAccessLink(account.id, adAccount.id, payload)
				else
					await updateFadsSettingsAccessLink(account.id, adAccount.id, payload)
				initialAccessId.current = form.accessLinkId
			}

			// A single authoritative refresh AFTER all writes, awaited so the
			// list reflects the saved state before we close. The sequence guard
			// in refreshAdAccounts ensures this (latest) result wins over any
			// stale in-flight refresh fired by the individual updates above.
			await refreshAdAccounts()

			// Only mark clean and close once every write succeeded. If a write
			// throws (e.g. the chosen credential is invalid), the store surfaces
			// an error toast; we keep the drawer open and the form dirty so the
			// user can retry — never close-as-if-saved (issue: silent no-save).
			setDirty(false)
			onClose()
		} catch {
			// Error already surfaced by the store; leave the drawer open.
		} finally {
			setSaving(false)
		}
	}

	const handleDelete = async () => {
		if (!account) return
		await deleteAdAccountAccount(adAccount.account_id, adAccount.id)
		setConfirmDelete(false)
		onClose()
	}

	const platformLabel =
		platform === 'GADS'
			? 'Google Ads'
			: platform === 'BADS'
			? 'Microsoft Ads'
			: 'Meta'

	// `{account name} ({account ID})` (§2.4), the same shape the campaign drawer's header
	// card uses. The id is dropped when it IS the name: `adAccountLabel` falls back to the
	// account number for the accounts that have no name, and "964-956-9197 (964-956-9197)"
	// is not what the ticket is asking for.
	const accountName = adAccountLabel(adAccount)
	const accountNumber = formatAccountId(adAccount)
	const headerTitle =
		accountNumber && accountNumber !== accountName
			? `${accountName} (${accountNumber})`
			: accountName

	return (
		<Tabs
			value={tab}
			onChange={(value) => setTab(value as 'setup' | 'protection')}
			size="sm"
		>
			<ConnectionsSheet
				open={open}
				onClose={onClose}
				title="Ad account settings"
				plain={tab === 'protection'}
				closeOnEscape={!confirmDelete}
				tabs={
					<TabList fullBleed>
						<Tab
							value="setup"
							label="Setup"
							id="ad-account-tab-setup"
							aria-controls="ad-account-panel-setup"
						/>
						<Tab
							value="protection"
							label="Protection"
							id="ad-account-tab-protection"
							aria-controls="ad-account-panel-protection"
						/>
					</TabList>
				}
				footer={
					<>
						<div
							className={
								tab === 'setup'
									? 'flex items-center justify-end gap-3'
									: 'hidden'
							}
						>
							<AppButton variant="secondary" label="Cancel" onClick={onClose} />
							<AppButton
								variant="primary"
								label="Save"
								disabled={!canWrite || !dirty}
								isLoading={saving}
								onClick={handleSave}
							/>
						</div>
						<div
							ref={setSaveSlot}
							className={
								tab === 'protection'
									? 'flex items-center justify-end gap-3'
									: 'hidden'
							}
						/>
					</>
				}
			>
				{/* BELOW the tab row and above everything else, so it is visible on both
				    tabs (§2.3). */}
				{!protectionOn && (
					<div className="pb-2">
						<ClickDetectionBanner scope="ad account" />
					</div>
				)}
				<div
					role="tabpanel"
					id="ad-account-panel-setup"
					aria-labelledby="ad-account-tab-setup"
					hidden={tab !== 'setup'}
				>
					<div className="flex flex-col gap-2">
						<ConnectionSection className="flex items-center justify-between gap-3">
							<div className="flex min-w-0 items-center gap-3">
								<IntegrationLogo
									provider={platformToProvider(platform)}
									size={24}
								/>
								<div className="flex min-w-0 flex-col">
									<span className="truncate text-base font-medium text-t-default">
										{headerTitle}
									</span>
									<span className="truncate text-sm text-t-secondary">
										{platformLabel}
									</span>
								</div>
							</div>
							<AppButton
								loader="dots"
								variant="secondary"
								label="Synchronize"
								disabled={!canWrite}
								isLoading={isLoading('ASSET_SYNC_ACCOUNT')}
								onClick={handleSync}
							/>
						</ConnectionSection>

						{form && (
							<>
								<ConnectionSection>
									<ConnectionSectionTitle
										title="Details"
										hint="The name, the formats, and the credential ClickGuard signs in with."
									/>
									<div className="flex flex-col gap-4">
										<Input
											state="default"
											label="Account name"
											size="sm"
											value={form.name}
											onChange={(e) => patch({ name: e.target.value })}
										/>

										<div>
											<p className="text-base font-medium text-[rgb(var(--cg-text-primary))] mb-1.5">
												Currency
											</p>
											<SearchableSelect
												className="w-full [&>button]:w-full [&>button]:!justify-between"
												options={currenciesOptions.map((c) => ({
													value: c,
													label: c
												}))}
												value={form.currency}
												placeholder="Select currency"
												menuWidth="trigger"
												portal
												onValueChange={(v) => patch({ currency: v })}
											/>
										</div>

										<div>
											<p className="text-base font-medium text-[rgb(var(--cg-text-primary))] mb-1.5">
												Timezone
											</p>
											<SearchableSelect
												className="w-full [&>button]:w-full [&>button]:!justify-between"
												options={tzList.map((t) => ({ value: t, label: t }))}
												value={form.timezone}
												placeholder="Select timezone"
												menuWidth="trigger"
												portal
												onValueChange={(v) => patch({ timezone: v })}
											/>
										</div>

										<div>
											<p className="text-base font-medium text-[rgb(var(--cg-text-primary))] mb-1.5">
												Credential
											</p>
											<div className="flex items-center gap-2">
												<div className="flex-1">
													<SearchableSelect
														className="w-full [&>button]:w-full [&>button]:!justify-between"
														options={linkOptions}
														value={form.accessLinkId}
														placeholder="Select a connection"
														menuWidth="trigger"
														portal
														onValueChange={(v) => patch({ accessLinkId: v })}
													/>
												</div>
												<AppButton
													loader="dots"
													variant="secondary"
													label="New credential"
													disabled={!canWrite}
													isLoading={awaitingCredential}
													onClick={() => {
														// Drop any stale code so we only pick up this flow's result.
														window.localStorage.removeItem(
															oauthCodeKeyFor(platform)
														)
														window.open(authUrlFor(platform), '_blank')
														setAwaitingCredential(true)
													}}
												/>
											</div>
											{/*
											 * Reflects the SAVED credential's access to this ad
											 * account (details.status === 'NO_ACCESS'). Hidden while an
											 * unsaved credential change is pending, since the new
											 * credential's access is unknown until it's saved.
											 */}
											{acc.details?.status === 'NO_ACCESS' &&
												form.accessLinkId === initialAccessId.current && (
													<p className="mt-1.5 text-sm font-normal text-[rgb(var(--cg-danger))]">
														Selected credential has lost access to this account
													</p>
												)}
										</div>
									</div>
								</ConnectionSection>
								<ConnectionSection>
									<ConnectionSectionTitle
										title="Exclusions"
										hint="How ClickGuard keeps a blocked visitor away from your ads."
									/>
									<div className="flex flex-col gap-4">
										{platform === 'GADS' && (
											<>
												<div>
													<div className="flex flex-col gap-2">
														<div className="flex items-center gap-2">
															<Checkbox
																size="sm"
																checked={form.ip_exclusions}
																label="Enable IP address exclusions"
																onChange={(e) =>
																	patch({ ip_exclusions: e.target.checked })
																}
															/>
															<Tooltip
																position="top"
																portal
																content="Enables excluding IP addresses and IP ranges on supported campaigns."
															>
																<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
															</Tooltip>
														</div>
														<div className="flex items-center gap-2">
															<Checkbox
																size="sm"
																checked={form.audience_exclusions}
																label="Enable audience exclusions"
																onChange={(e) =>
																	patch({
																		audience_exclusions: e.target.checked
																	})
																}
															/>
															<Tooltip
																position="top"
																portal
																content="Enables creating and maintaining the user list that we’ll exclude on all your campaigns. Adding visitors to the user list will automatically exclude them from seeing your ads."
															>
																<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
															</Tooltip>
														</div>
													</div>
												</div>
											</>
										)}

										{(platform === 'BADS' || platform === 'FADS') && (
											<Checkbox
												checked={form.set_custom_audience}
												label={
													platform === 'FADS'
														? 'Automatically set excluded audience to ad sets'
														: 'Automatically set excluded audience to campaigns'
												}
												onChange={(e) =>
													patch({ set_custom_audience: e.target.checked })
												}
											/>
										)}

										{platform === 'BADS' && (
											<>
												<div>
													<div className="flex items-center gap-2 mb-1.5">
														<p className="text-base font-medium text-[rgb(var(--cg-text-primary))]">
															Audience exclusion period
														</p>
														<Tooltip
															position="top"
															portal
															content="How long a suspicious visitor should be prevented from seeing your ads. Max duration of 390 days"
														>
															<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
														</Tooltip>
													</div>
													<div className="flex items-center gap-2">
														<Input
															state="default"
															size="sm"
															type="number"
															value={form.period}
															className="w-24"
															onChange={(e) =>
																patch({ period: e.target.value })
															}
														/>
														<span className="text-sm text-t-secondary">
															Days
														</span>
													</div>
												</div>
												<div>
													<div className="flex items-center gap-2">
														<span className="font-medium">UET tag</span>
														<Tooltip
															position="top"
															portal
															content="UET tag that is installed in your website"
														>
															<AiOutlineQuestionCircle className="text-gray-500 h-4 w-4" />
														</Tooltip>
													</div>
													<div className="w-full">
														<p className="text-t-checkbox-unactive">
															{(
																adAccount as BingAdsAdAccountDTO
															).details?.uet_details?.name?.replace(
																'ClickGuard',
																'ClickGUARD'
															)}
														</p>
													</div>
												</div>
												<div className="flex flex-col gap-1 py-3">
													<div className="flex items-center gap-2">
														<span className="font-medium">
															UET Tracking Script
														</span>
														<Tooltip
															position="top"
															portal
															content="UET Tracking Script allow ClickGUARD to create Audience Exclusions. Copy the tag and paste it in between the <head></head> tags of every page of your website."
														>
															<AiOutlineQuestionCircle className="text-gray-500 h-4 w-4" />
														</Tooltip>
													</div>
													<div className="w-full">
														<TrackerCodeTemplate
															isCopy={true}
															content={{
																subtitle: 'Tracking script',
																code: (adAccount as BingAdsAdAccountDTO).details
																	?.uet_details?.TrackingScript
																	? (adAccount as BingAdsAdAccountDTO).details
																			?.uet_details?.TrackingScript
																	: '< code sample >'
															}}
														/>
													</div>
												</div>
											</>
										)}

										{platform === 'FADS' && (
											<>
												<div>
													<div className="flex items-center gap-2 mb-1.5">
														<p className="text-base font-medium text-[rgb(var(--cg-text-primary))]">
															Audience exclusion period
														</p>
														<Tooltip
															position="top"
															portal
															content="How long a suspicious visitor should be prevented from seeing your ads"
														>
															<AiOutlineQuestionCircle className="text-t-gray-hold h-4 w-4" />
														</Tooltip>
													</div>
													<div className="flex items-center gap-2">
														<Input
															state="default"
															size="sm"
															type="number"
															value={form.period}
															className="w-24"
															onChange={(e) =>
																patch({ period: e.target.value })
															}
														/>
														<SearchableSelect
															options={PERIOD_UNITS.map((u) => ({
																value: u,
																label: u
															}))}
															className="w-full [&>button]:w-full [&>button]:!justify-between"
															value={form.periodUnit}
															menuWidth="trigger"
															portal
															onValueChange={(v) => patch({ periodUnit: v })}
														/>
													</div>
												</div>
												{showPixel && (
													<div className="flex flex-col gap-1">
														<div className="flex items-center gap-2">
															<span className="font-medium">
																Selected pixel
															</span>
															<Tooltip
																position="top"
																portal
																content="Web ID to be used to track your audience behaviour"
															>
																<HelpCircle className="text-t-gray-hold h-4 w-4" />
															</Tooltip>
														</div>
														<div className="w-full">
															<SearchableSelect
																options={pixelOptions}
																className="w-full [&>button]:w-full [&>button]:!justify-between"
																value={form.pixel_id}
																placeholder="Select a pixel"
																menuWidth="trigger"
																portal
																onValueChange={(e) => patch({ pixel_id: e })}
																filter={(item, query) =>
																	item.value
																		.toLowerCase()
																		.includes(query.toLocaleLowerCase())
																}
															/>
														</div>
													</div>
												)}
											</>
										)}
									</div>
								</ConnectionSection>
							</>
						)}

						{(platform === 'GADS' || platform === 'BADS') && (
							<TrackingSetupCard
								adAccount={adAccount}
								accountId={account?.id}
								assetId={asset?.id}
								open={open}
								canWrite={canWrite}
								onClose={onClose}
							/>
						)}
						<ConnectionSection>
							<ConnectionSectionTitle
								title="Danger zone"
								hint="This removes the account from this website. It does not touch the account on the ad platform."
							/>
							<div className="flex items-center gap-6">
								<span className="flex-1 text-base font-medium text-[rgb(var(--cg-text-primary))]">
									Delete account
								</span>
								<AppButton
									variant="destructive"
									label="Delete"
									disabled={!canWrite}
									onClick={() => setConfirmDelete(true)}
								/>
							</div>
						</ConnectionSection>
					</div>
				</div>

				<div
					role="tabpanel"
					id="ad-account-panel-protection"
					aria-labelledby="ad-account-tab-protection"
					hidden={tab !== 'protection'}
				>
					<div className="flex flex-col gap-4">
						{/* Ticking this hands the account back to the website. Unticking it
						    seeds the account's own set from what it was inheriting, so the
						    user starts from what they can see rather than from defaults. */}
						<div className="flex flex-col gap-1 border-b border-[rgb(var(--cg-border))] pb-4">
							<Checkbox
								size="sm"
								id="ad-account-inherit-protection"
								checked={inherit}
								disabled={!canWrite}
								label="Inherit website protection settings"
								onChange={(e) => setInherit(e.target.checked)}
							/>
							<p className="pl-6 text-[12px] leading-4 text-t-secondary">
								This account follows the website settings on the{' '}
								<Link
									to={`/workspace/${account?.id}/asset/${asset?.id}/ai-protection`}
									className="font-medium text-[rgb(var(--cg-text-link))] underline underline-offset-2"
								>
									AI Protection
								</Link>{' '}
								page. Untick the box to give this account its own settings.
							</p>
						</div>

						<ProtectionSettingsCard
							// Remount when the entity changes or the tick flips, because
							// `initialValues` is read once — which is also what discards an
							// unsaved draft on re-tick.
							key={`${adAccount.id}-${inherit}`}
							bare
							scope="ad account"
							inherited={inherit}
							savedInherited={inheritsProtection(acc)}
							parentValues={websiteSettings}
							initialValues={
								ownsProtection(acc)
									? protectionToSettings(acc.protection)
									: websiteSettings
							}
							saveSlot={saveSlot}
							onSave={handleSaveProtection}
						/>
					</div>
				</div>

				<ConfirmDialog
					open={confirmDelete}
					onClose={() => setConfirmDelete(false)}
					onConfirm={handleDelete}
					tone="danger"
					title={`Remove ${adAccountLabel(adAccount)}`}
					description="Are you sure you want to remove this ad account from this website?"
					confirmLabel="Delete"
					loading={isLoading('ASSET_DELETE_AD_ACCOUNT')}
				/>
			</ConnectionsSheet>
		</Tabs>
	)
}
